import React, { FC, useCallback } from 'react';
import { ButtonV2, DrawerV2, Flex, PopoverV2 } from '@withjoy/joykit';
import { useDisclosure } from '@withjoy/joykit';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { createContext } from '@shared/utils/createContext';
import { createDisplayName } from '@withjoy/joykit/utils';
import { forwardRef } from '@shared/utils/forwardRef';
import { pxToRem } from '@withjoy/joykit/theme';
import type {
  ResponsiveMenuV2Props,
  ResponsiveMenuV2ContextValue,
  ResponsiveMenuV2TriggerProps,
  ResponsiveMenuV2ContentProps,
  ResponsiveMenuV2ItemProps,
} from './ResponsiveMenuV2.types';

const [ResponsiveMenuProvider, useResponsiveMenuContext] = createContext<ResponsiveMenuV2ContextValue>({
  name: 'ResponsiveMenuV2',
});

const ResponsiveMenuV2Trigger: FC<ResponsiveMenuV2TriggerProps> = ({ children }) => {
  const { isMobile, onOpen } = useResponsiveMenuContext();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const trigger: any = React.Children.only(children);

  if (isMobile) {
    const clonedTrigger = React.cloneElement(trigger, {
      ...trigger.props,
      onClick: (e: React.MouseEvent) => {
        trigger.props?.onClick?.(e);
        onOpen();
      },
    });

    return clonedTrigger;
  }

  return <PopoverV2.Trigger>{children}</PopoverV2.Trigger>;
};

const ResponsiveMenuV2Content = forwardRef<'div', ResponsiveMenuV2ContentProps>((props, ref) => {
  const { children, ...restProps } = props;
  const { isMobile } = useResponsiveMenuContext();

  if (isMobile) {
    return (
      <DrawerV2.Body padding={6}>
        <Flex
          flexDirection="column"
          gap={1}
        >
          {children}
        </Flex>
      </DrawerV2.Body>
    );
  }

  return (
    <PopoverV2.Content
      borderRadius={pxToRem(24)}
      backgroundColor="white"
      padding={6}
      boxShadow="0px 9px 36px -30px rgba(0, 0, 0, 0.04), 0px 10px 60px -20px rgba(44, 41, 37, 0.03), 0px 3px 8px 0px rgba(0, 0, 0, 0.03)"
      {...restProps}
      ref={ref}
    >
      <Flex
        flexDirection="column"
        gap={1}
      >
        {children}
      </Flex>
    </PopoverV2.Content>
  );
});

const ResponsiveMenuV2Item: FC<ResponsiveMenuV2ItemProps> = ({ children, onClick, closeOnClick = true, ...buttonProps }) => {
  const { onClose } = useResponsiveMenuContext();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);

      if (closeOnClick) {
        onClose();
      }
    },
    [onClose, closeOnClick],
  );

  return (
    <ButtonV2
      padding={5}
      height="auto"
      size="sm"
      variant="ghost"
      intent="neutral"
      justifyContent="flex-start"
      minWidth={pxToRem(192)}
      color="mono14"
      borderRadius={pxToRem(4)}
      _hover={{
        backgroundColor: 'mono2',
        color: 'mono14',
      }}
      onClick={handleClick}
      {...buttonProps}
    >
      {children}
    </ButtonV2>
  );
};

const ResponsiveMenuV2Component: FC<ResponsiveMenuV2Props> = ({
  placement = 'bottom',
  isOpen: isOpenProp,
  onClose: onCloseProp,
  onOpen: onOpenProp,
  drawerOverrides,
  popperOptions,
  children,
}) => {
  const isMobile = useIsMobileScreen();

  const { isOpen, onClose, onOpen } = useDisclosure({
    isOpen: isOpenProp,
    onClose: onCloseProp,
    onOpen: onOpenProp,
  });

  if (isMobile) {
    const defaultDrawerOverrides = {
      Content: {
        props: {
          boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          borderTopLeftRadius: pxToRem(16),
          borderTopRightRadius: pxToRem(16),
          height: 'auto',
          width: '100%',
        },
      },
    };

    const mergedOverrides = drawerOverrides
      ? {
          ...drawerOverrides,
          Content: {
            props: {
              ...defaultDrawerOverrides.Content.props,
              ...drawerOverrides.Content?.props,
            },
          },
        }
      : defaultDrawerOverrides;

    const array = React.Children.toArray(children);
    const trigger = array.find(child => React.isValidElement(child) && child.type === ResponsiveMenuV2Trigger);
    const content = array.filter(child => React.isValidElement(child) && child.type === ResponsiveMenuV2Content);

    return (
      // Intentional: Container prevents menu click events from propagating outside the menu.
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div onClick={event => event.stopPropagation()}>
        <ResponsiveMenuProvider value={{ isOpen, onClose, onOpen, isMobile }}>
          {trigger}
          <DrawerV2
            isOpen={isOpen}
            onClose={onClose}
            overrides={mergedOverrides}
            anchor="bottom"
            useBackdrop={false}
          >
            {content}
          </DrawerV2>
        </ResponsiveMenuProvider>
      </div>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={event => event.stopPropagation()}>
      <PopoverV2
        placement={placement}
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        popperOptions={popperOptions}
      >
        <ResponsiveMenuProvider value={{ isOpen, onClose, onOpen, isMobile }}>{children}</ResponsiveMenuProvider>
      </PopoverV2>
    </div>
  );
};

const ResponsiveMenuV2 = Object.assign(ResponsiveMenuV2Component, {
  Trigger: ResponsiveMenuV2Trigger,
  Content: ResponsiveMenuV2Content,
  Item: ResponsiveMenuV2Item,
});

ResponsiveMenuV2.displayName = createDisplayName('ResponsiveMenuV2');
ResponsiveMenuV2Trigger.displayName = createDisplayName('ResponsiveMenuV2Trigger');
ResponsiveMenuV2Content.displayName = createDisplayName('ResponsiveMenuV2Content');
ResponsiveMenuV2Item.displayName = createDisplayName('ResponsiveMenuV2Item');

export { ResponsiveMenuV2, useResponsiveMenuContext };
